import type { RankingTypes } from "@teamz/common";
import type { ComponentProps } from "react";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATP_WTA_ITF_ErrorMsg, ATP_WTA_ITF_Regex, bestRankingYearSchema, playerFFTranking, requiredMsg, UTR_ErrorMsg, UTRRegex } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { z } from "zod";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useUserRole } from "@/hooks/use-user-role";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { Switch } from "../ui/switch";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const EmptyButton = ({ ref, children, ...rest }: ComponentProps<"button">) => {
  const { user } = useMe();

  return (
    <button
      type="button"
      className={cn(
        "flex w-full flex-col bg-neutral-50 first:rounded-l-2xl last:rounded-r-2xl",
        user.role === "club" && !user.isAdmin && "pointer-events-none",
      )}
      ref={ref}
      {...rest}
    >
      <div className="flex h-[76px] w-full flex-col items-center justify-center pt-4">
        {(user.role !== "club" || user.isAdmin) ? <AddIcon className="size-6 text-orange-500" /> : <DotsIcon />}
      </div>
      <div className="flex w-full items-center justify-center p-2">
        <span className="text-center text-sm font-semibold text-neutral-450">{children}</span>
      </div>
    </button>
  );
};

type RankingButtonProps = { rankingType: RankingTypes; currentRanking?: string | null; bestRanking?: string | null; bestYear?: number | null; lastRanking?: string | null; lastYear?: number | null; hideBest?: boolean };
const RankingButton = ({ ref, rankingType, currentRanking, bestRanking, bestYear, lastRanking, lastYear, hideBest, ...rest }: ComponentProps<"button"> & RankingButtonProps) => {
  const isCurrentRankingBest = !(bestRanking && bestYear);
  const isLastRanking = lastRanking && lastYear;
  const { user } = useMe();

  return (
    <button

      type="button"
      className={cn(
        "flex w-full flex-col bg-white outline-orange-500 first:rounded-l-2xl last:rounded-r-2xl",
        user.role === "club" && !user.isAdmin && "pointer-events-none",
      )}
      ref={ref}
      {...rest}
    >
      <div className="flex h-[76px] w-full flex-col items-center justify-center gap-1 pt-4">
        {(currentRanking && !lastRanking) && <span className="text-[22px] font-bold leading-none text-neutral-900">{currentRanking}</span>}
        {isLastRanking
          ? (
              <>
                <span className="text-[22px] font-bold leading-none text-neutral-900">
                  -
                </span>

                <span className="text-xs text-orange-500">
                  <span>{lastRanking}</span>
                  <span className="text-neutral-500">{` (${lastYear})`}</span>
                </span>

              </>
            )
          // Classement servi en tranche : le meilleur classement a été retiré
          // par l'API, il n'y a donc ni valeur ni libellé « Best » à afficher.
          : hideBest
            ? null
            : isCurrentRankingBest
              ? <span className="text-xs text-orange-500">Best</span>
              : (
                  <span className="text-xs text-orange-500">
                    {bestRanking}
                    <span className="text-neutral-500">{` (${bestYear})`}</span>
                  </span>
                )}
      </div>
      <div className={cn("flex w-full items-center justify-between p-2", user.role === "club" && !user.isAdmin && "items-center justify-center",
      )}
      >
        <span className="text-center text-sm font-semibold text-neutral-900">{rankingType === "ITF" ? "ITF JR" : rankingType}</span>
        {(user.role !== "club" || user.isAdmin) && <EditIcon className="size-5 text-neutral-500" />}
      </div>
    </button>
  );
};

const today = new Date().toISOString().split("T")[0];
const currentYear = new Date().getFullYear();

const updatePlayerFFTRankingDiscriminatedUnionSchema = z.discriminatedUnion("isCurrentFFTRankingBest", [
  z.object({
    isCurrentFFTRankingBest: z.literal(true),
    currentFFTRanking: z.enum(playerFFTranking, { message: requiredMsg }),
    currentFFTRankingNumber: z.coerce.number().nullable(),
    bestFFTRanking: z.enum(playerFFTranking).nullable(),
    bestFFTRankingYear: bestRankingYearSchema.nullable(),
    bestFFTRankingNumber: z.coerce.number().nullable(),
  }),
  z.object({
    isCurrentFFTRankingBest: z.literal(false),
    currentFFTRanking: z.enum(playerFFTranking, { message: requiredMsg }),
    currentFFTRankingNumber: z.coerce.number().nullable(),
    bestFFTRanking: z.enum(playerFFTranking, { message: requiredMsg }),
    bestFFTRankingYear: bestRankingYearSchema,
    bestFFTRankingNumber: z.coerce.number().nullable(),
  }),
]).superRefine((data, ctx) => {
  if (data.bestFFTRanking === "T100" && !data.bestFFTRankingNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMsg,
      path: ["bestFFTRankingNumber"],
    });
  }

  if (data.currentFFTRanking === "T100" && !data.currentFFTRankingNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMsg,
      path: ["currentFFTRankingNumber"],
    });
  }
});
type UpdatePlayerFFTRankingDiscriminatedUnionSchema = z.infer<typeof updatePlayerFFTRankingDiscriminatedUnionSchema>;

const updatePlayerUTRRankingDiscriminatedUnionSchema = z.discriminatedUnion("isCurrentUTRRankingBest", [
  z.object({
    isCurrentUTRRankingBest: z.literal(true),
    currentUTRRanking: z.string({ message: requiredMsg }).regex(UTRRegex, { message: UTR_ErrorMsg }),
    bestUTRRanking: z.string().nullable(),
    bestUTRRankingYear: bestRankingYearSchema.nullable(),
  }),
  z.object({
    isCurrentUTRRankingBest: z.literal(false),
    currentUTRRanking: z.string({ message: requiredMsg }).regex(UTRRegex, { message: UTR_ErrorMsg }),
    bestUTRRanking: z.string({ message: requiredMsg }).regex(UTRRegex, { message: UTR_ErrorMsg }),
    bestUTRRankingYear: bestRankingYearSchema,
  }),
]);
type UpdatePlayerUTRRankingDiscriminatedUnionSchema = z.infer<typeof updatePlayerUTRRankingDiscriminatedUnionSchema>;

const updatePlayerATPWTARankingDiscriminatedUnionSchema = z.union([
  z.object({
    isPlayerHaveATPWTARanking: z.literal(true),
    isCurrentATPWTARankingBest: z.literal(true),
    currentATPWTARanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    bestATPWTARanking: z.string().nullable(),
    bestATPWTARankingYear: bestRankingYearSchema.nullable(),
    lastATPWTARanking: z.string().nullable(),
    lastATPWTARankingYear: bestRankingYearSchema.nullable(),
  }),
  z.object({
    isPlayerHaveATPWTARanking: z.literal(true),
    isCurrentATPWTARankingBest: z.literal(false),
    currentATPWTARanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    bestATPWTARanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    bestATPWTARankingYear: bestRankingYearSchema,
    lastATPWTARanking: z.string().nullable(),
    lastATPWTARankingYear: bestRankingYearSchema.nullable(),
  }),
  z.object({
    isPlayerHaveATPWTARanking: z.literal(false),
    isCurrentATPWTARankingBest: z.literal(false),
    currentATPWTARanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg })
      .nullable(),
    bestATPWTARanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg })
      .nullable(),
    bestATPWTARankingYear: bestRankingYearSchema.nullable(),
    lastATPWTARanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    lastATPWTARankingYear: bestRankingYearSchema,
  }),
]);
type UpdatePlayerATPWTARankingDiscriminatedUnionSchema = z.infer<typeof updatePlayerATPWTARankingDiscriminatedUnionSchema>;

const updatePlayerITFRankingDiscriminatedUnionSchema = z.union([
  z.object({
    isPlayerHaveITFRanking: z.literal(true),
    isCurrentITFRankingBest: z.literal(true),
    currentITFRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    bestITFRanking: z.string().nullable(),
    bestITFRankingYear: bestRankingYearSchema.nullable(),
    lastITFRanking: z.string().nullable(),
    lastITFRankingYear: bestRankingYearSchema.nullable(),
  }),
  z.object({
    isPlayerHaveITFRanking: z.literal(true),
    isCurrentITFRankingBest: z.literal(false),
    currentITFRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    bestITFRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    bestITFRankingYear: bestRankingYearSchema,
    lastITFRanking: z.string().nullable(),
    lastITFRankingYear: bestRankingYearSchema.nullable(),
  }),
  z.object({
    isPlayerHaveITFRanking: z.literal(false),
    isCurrentITFRankingBest: z.literal(false),
    currentITFRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg })
      .nullable(),
    bestITFRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg })
      .nullable(),
    bestITFRankingYear: bestRankingYearSchema.nullable(),
    lastITFRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    lastITFRankingYear: bestRankingYearSchema,
  }),
]);
type UpdatePlayerITFRankingDiscriminatedUnionSchema = z.infer<typeof updatePlayerITFRankingDiscriminatedUnionSchema>;

function FFTButton({ openModal, player }: { openModal: () => void; player: RouterOutputs["player"]["getPlayer"] }) {
  return (
    <>
      {player.FFTRanking
        ? (
            <RankingButton
              rankingType="FFT"
              onClick={openModal}
              bestRanking={player.bestFFTRanking === "T100" && player.bestFFTRankingNumber ? `N${player.bestFFTRankingNumber}` : player.bestFFTRanking}
              bestYear={player.bestFFTRankingYear}
              currentRanking={player.FFTRanking === "T100" && player.FFTRankingNumber ? `N${player.FFTRankingNumber}` : player.FFTRanking}
            />
          )
        : <EmptyButton onClick={openModal}>FFT</EmptyButton>}
    </>
  );
}

function UTRButton({ openModal, player }: { openModal: () => void; player: RouterOutputs["player"]["getPlayer"] }) {
  const { t } = useTranslation(["common"]);
  // Fiche anonymisée : l'API n'envoie pas la valeur exacte mais la tranche, la
  // même que celle affichée dans la liste teaser.
  const currentRanking = player.UTRBracket
    ? tDyn(t, `common:ranking-bracket.utr.${player.UTRBracket}`)
    : player.UTRRanking;

  return (
    <>
      {currentRanking
        ? (
            <RankingButton
              rankingType="UTR"
              onClick={openModal}
              hideBest={Boolean(player.UTRBracket)}
              bestRanking={player.bestUTRRanking}
              bestYear={player.bestUTRRankingYear}
              currentRanking={currentRanking}
            />
          )
        : <EmptyButton onClick={openModal}>UTR</EmptyButton>}
    </>
  );
}

function ATPWTAButton({ openModal, player }: { openModal: () => void; player: RouterOutputs["player"]["getPlayer"] }) {
  const { t } = useTranslation(["common"]);
  const currentRanking = player.ATPBracket
    ? tDyn(t, `common:ranking-bracket.atp.${player.ATPBracket}`)
    : player.ATPWTARanking;

  return (
    <>
      {currentRanking || player.lastATPWTARanking
        ? (
            <RankingButton
              rankingType={player.gender === "female" ? "WTA" : "ATP"}
              onClick={openModal}
              hideBest={Boolean(player.ATPBracket)}
              bestRanking={player.bestATPWTARanking}
              bestYear={player.bestATPWTARankingYear}
              currentRanking={currentRanking}
              lastRanking={player.lastATPWTARanking}
              lastYear={player.lastATPWTARankingYear}
            />
          )
        : <EmptyButton onClick={openModal}>{player.gender === "female" ? "WTA" : "ATP"}</EmptyButton>}
    </>
  );
}

function ITFButton({ openModal, player }: { openModal: () => void; player: RouterOutputs["player"]["getPlayer"] }) {
  const { t } = useTranslation(["common"]);
  // ITF junior est numéroté comme l'ATP, il partage donc ses tranches.
  const currentRanking = player.ITFBracket
    ? tDyn(t, `common:ranking-bracket.atp.${player.ITFBracket}`)
    : player.ITFRanking;

  return (
    <>
      {currentRanking || player.lastITFRanking
        ? (
            <RankingButton
              rankingType="ITF"
              onClick={openModal}
              hideBest={Boolean(player.ITFBracket)}
              bestRanking={player.bestITFRanking}
              bestYear={player.bestITFRankingYear}
              currentRanking={currentRanking}
              lastRanking={player.lastITFRanking}
              lastYear={player.lastITFRankingYear}
            />
          )
        : <EmptyButton onClick={openModal}>ITF JR</EmptyButton>}
    </>
  );
}

export function PlayerRanking({ player }: { player: RouterOutputs["player"]["getPlayer"] }) {
  const [modalState, updateModalState] = useState<RankingTypes | "close">("close");
  const { t } = useTranslation(["common"]);
  const userRole = useUserRole();
  const { user } = useMe();

  const isModalOpen = modalState !== "close";
  function closeModal() {
    updateModalState("close");
  }

  return (
    <div className="flex flex-col gap-2">
      <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">{t("common:rankings")}</h3>

      <div className="rounded-[20px] bg-white p-2">
        <div className="flex w-full divide-x rounded-xl border border-neutral-400 first:rounded-l-xl last:rounded-r-xl">
          <FFTButton
            player={player}
            openModal={() => {
              updateModalState("FFT");
            }}
          />
          <UTRButton
            player={player}
            openModal={() => {
              updateModalState("UTR");
            }}
          />
          <ATPWTAButton
            player={player}
            openModal={() => {
              updateModalState(player.gender === "female" ? "WTA" : "ATP");
            }}
          />
          <ITFButton
            player={player}
            openModal={() => {
              updateModalState("ITF");
            }}
          />
        </div>

        {(userRole !== "club" || user.isAdmin) && (
          <Sheet open={isModalOpen} onOpenChange={closeModal}>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>
                  {t("common:ranking")}
                  {" "}
                  { modalState === "ITF" ? "ITF Junior" : modalState }
                </SheetTitle>
              </SheetHeader>

              {modalState === "FFT" && <FFTRankingForm closeModal={closeModal} />}
              {modalState === "UTR" && <UTRRankingForm closeModal={closeModal} />}
              {(modalState === "ATP" || modalState === "WTA") && <ATPWTARankingForm closeModal={closeModal} />}
              {modalState === "ITF" && <ITFRankingForm closeModal={closeModal} />}

            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}

function FFTRankingForm({ closeModal }: { closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const { id } = useParams();
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data, status } = useQuery(trpc.player.getPlayer.queryOptions({ playerId: id || user.id }));

  const { mutate, isPending } = useMutation(trpc.player.updateFFTRanking.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerRankingForm = useForm<UpdatePlayerFFTRankingDiscriminatedUnionSchema>({
    resolver: zodResolver(updatePlayerFFTRankingDiscriminatedUnionSchema),
    // @ts-expect-error - FFTRanking is null or set, we want to force to undefined
    defaultValues: {
      isCurrentFFTRankingBest: data?.FFTRanking !== null && !data?.bestFFTRanking && !data?.bestFFTRankingYear,
      currentFFTRanking: data?.FFTRanking || undefined,
      bestFFTRanking: data?.bestFFTRanking,
      bestFFTRankingYear: data?.bestFFTRankingYear,
      currentFFTRankingNumber: data?.FFTRankingNumber,
      bestFFTRankingNumber: data?.bestFFTRankingNumber,
    },
  });

  const onSubmit = playerRankingForm.handleSubmit((data) => {
    mutate({
      userId: id || user.id,
      updatePlayerFFTRankingSchema: {
        currentFFTRanking: data.currentFFTRanking,
        currentFFTRankingNumber: data.currentFFTRankingNumber,
        bestFFTRanking: data.bestFFTRanking,
        bestFFTRankingYear: data.bestFFTRankingYear,
        bestFFTRankingNumber: data.bestFFTRankingNumber,
      },
    });
  });

  const onClear = () => {
    mutate({
      userId: id || user.id,
      updatePlayerFFTRankingSchema: {
        currentFFTRanking: null,
        currentFFTRankingNumber: null,
        bestFFTRanking: null,
        bestFFTRankingYear: null,
        bestFFTRankingNumber: null,
      },
    });
  };

  if (status !== "success") {
    return null;
  }

  const FFTFormErrors = playerRankingForm.formState.errors;

  return (
    <>
      <SheetForm id="PlayerRankingForm" onSubmit={onSubmit}>
        <Controller
          control={playerRankingForm.control}
          name="currentFFTRanking"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:current-ranking", { ranking: "FFT" })}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerFFTranking.map(ranking => (
                      <SelectItem key={ranking} value={ranking}>{ranking === "T100" ? `${t("profile:numbered")} - T100` : ranking}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        {playerRankingForm.watch("currentFFTRanking") === "T100" && (
          <TextField
            type="number"
            helper={t("profile:ranking-numbered-helper")}
            prefix={<span className="text-sm font-bold">N</span>}
            label={t("profile:numbered")}
            max={100}
            min={1}
            {...playerRankingForm.register("currentFFTRankingNumber")}
            errorMessage={tDyn(t, FFTFormErrors.currentFFTRankingNumber?.message)}
          />
        )}

        <Controller
          control={playerRankingForm.control}
          name="isCurrentFFTRankingBest"
          rules={{
            onChange: (value) => {
              if (value) {
                playerRankingForm.setValue("bestFFTRanking", null);
                playerRankingForm.setValue("bestFFTRankingYear", null);
                playerRankingForm.setValue("bestFFTRankingNumber", null);
              }
            },
          }}
          render={({ field }) => (
            <RootField>
              <Checkbox label={t("profile:my-best-ranking")} checked={field.value} onCheckedChange={field.onChange} />
            </RootField>
          )}
        />

        {!playerRankingForm.watch("isCurrentFFTRankingBest") && (
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div className="flex flex-col gap-4">
              <Controller
                control={playerRankingForm.control}
                name="bestFFTRanking"
                render={({ field, fieldState }) => (
                  <RootField>
                    <Label>{t("profile:best-ranking", { ranking: "FFT" })}</Label>
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {playerFFTranking.map(ranking => (
                            <SelectItem key={ranking} value={ranking}>{ranking === "T100" ? `${t("profile:numbered")} - T100` : ranking}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {fieldState.error?.message && (
                      <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
                    )}
                  </RootField>
                )}
              />
              {playerRankingForm.watch("bestFFTRanking") === "T100" && (
                <TextField
                  type="number"
                  helper={t("profile:ranking-numbered-helper")}
                  prefix={<span className="text-sm font-bold">N</span>}
                  label={t("profile:numbered")}
                  max={100}
                  min={1}
                  {...playerRankingForm.register("bestFFTRankingNumber")}
                  errorMessage={tDyn(t, FFTFormErrors.bestFFTRankingNumber?.message)}
                />
              )}
            </div>
            <TextField
              type="number"
              label={t("common:year")}
              max={today}
              min={1950}
              {...playerRankingForm.register("bestFFTRankingYear")}
              errorMessage={tDyn(t, FFTFormErrors.bestFFTRankingYear?.message)}
            />
          </div>
        )}

      </SheetForm>

      { data.FFTRanking && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-ranking")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerRankingForm" loading={isPending || playerRankingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function UTRRankingForm({ closeModal }: { closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const { id } = useParams();
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data, status } = useQuery(trpc.player.getPlayer.queryOptions({ playerId: id || user.id }));

  const { mutate, isPending } = useMutation(trpc.player.updateUTRRanking.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerUTRRankingForm = useForm<UpdatePlayerUTRRankingDiscriminatedUnionSchema>({
    resolver: zodResolver(updatePlayerUTRRankingDiscriminatedUnionSchema),
    // @ts-expect-error - FFTRanking is null or set, we want to force to undefined
    defaultValues: {
      isCurrentUTRRankingBest: data?.UTRRanking !== null && !data?.bestUTRRanking && !data?.bestUTRRankingYear,
      currentUTRRanking: data?.UTRRanking || undefined,
      bestUTRRanking: data?.bestUTRRanking || undefined,
      bestUTRRankingYear: data?.bestUTRRankingYear,
    },
  });

  const onSubmit = playerUTRRankingForm.handleSubmit((data) => {
    mutate({
      userId: id || user.id,
      updatePlayerUTRRankingSchema: {
        currentUTRRanking: data.currentUTRRanking,
        bestUTRRanking: data.bestUTRRanking,
        bestUTRRankingYear: data.bestUTRRankingYear,
      },
    });
  });

  const onClear = () => {
    mutate(
      {
        userId: id || user.id,
        updatePlayerUTRRankingSchema: {
          currentUTRRanking: null,
          bestUTRRanking: null,
          bestUTRRankingYear: null,
        },
      },
    );
  };

  if (status !== "success") {
    return null;
  }

  const UTRFormErrors = playerUTRRankingForm.formState.errors;

  return (
    <>
      <SheetForm id="PlayerUTRRankingForm" onSubmit={onSubmit}>
        <Controller
          name="currentUTRRanking"
          control={playerUTRRankingForm.control}
          render={({ field }) => (
            <TextField
              helper="ex: 12.34, 1.23"
              label={t("profile:current-ranking", { ranking: "UTR" })}
              value={field.value}
              onChange={(event) => {
                field.onChange(event.target.value);
              }}
              errorMessage={tDyn(t, UTRFormErrors.currentUTRRanking?.message)}
            />
          )}
        />

        <Controller
          control={playerUTRRankingForm.control}
          name="isCurrentUTRRankingBest"
          rules={{
            onChange: (value) => {
              if (value) {
                playerUTRRankingForm.setValue("bestUTRRanking", null);
                playerUTRRankingForm.setValue("bestUTRRankingYear", null);
              }
            },
          }}
          render={({ field }) => (
            <RootField>
              <Checkbox label={t("profile:my-best-ranking")} checked={field.value} onCheckedChange={field.onChange} />
            </RootField>
          )}
        />

        {!playerUTRRankingForm.watch("isCurrentUTRRankingBest") && (
          <div className="grid grid-cols-[1fr_120px] items-end gap-3">
            <div className="flex flex-col gap-4">
              <Controller
                name="bestUTRRanking"
                control={playerUTRRankingForm.control}
                render={({ field }) => (
                  <TextField
                    helper="ex: 12.34, 1.23"
                    label={t("profile:best-ranking", { ranking: "UTR" })}
                    value={field.value || undefined}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                    }}
                    errorMessage={tDyn(t, UTRFormErrors.bestUTRRanking?.message)}
                  />
                )}
              />
            </div>
            <TextField
              type="number"
              helper={`1950 à ${currentYear}`}
              label={t("common:year")}
              max={today}
              min={1950}
              {...playerUTRRankingForm.register("bestUTRRankingYear")}
              errorMessage={tDyn(t, UTRFormErrors.bestUTRRankingYear?.message)}
            />
          </div>

        )}

      </SheetForm>

      { data.UTRRanking
      && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-ranking")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerUTRRankingForm" loading={isPending || playerUTRRankingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function ATPWTARankingForm({ closeModal }: { closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const { id } = useParams();
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data, status } = useQuery(trpc.player.getPlayer.queryOptions({ playerId: id || user.id }));

  const { mutate, isPending } = useMutation(trpc.player.updateATPWTARanking.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerATPWTARankingForm = useForm<UpdatePlayerATPWTARankingDiscriminatedUnionSchema>({
    resolver: zodResolver(updatePlayerATPWTARankingDiscriminatedUnionSchema),
    // @ts-expect-error - FFTRanking is null or set, we want to force to undefined
    defaultValues: {
      isPlayerHaveATPWTARanking: !data?.lastATPWTARanking,
      isCurrentATPWTARankingBest: data?.ATPWTARanking !== null && !data?.bestATPWTARanking && !data?.bestATPWTARankingYear,
      currentATPWTARanking: data?.ATPWTARanking || null,
      bestATPWTARanking: data?.bestATPWTARanking,
      bestATPWTARankingYear: data?.bestATPWTARankingYear,
      lastATPWTARanking: data?.lastATPWTARanking,
      lastATPWTARankingYear: data?.lastATPWTARankingYear,
    },
  });

  const onSubmit = playerATPWTARankingForm.handleSubmit((data) => {
    mutate({
      userId: id || user.id,
      updatePlayerATPWTARankingSchema: {
        currentATPWTARanking: data.currentATPWTARanking,
        bestATPWTARanking: data.bestATPWTARanking,
        bestATPWTARankingYear: data.bestATPWTARankingYear,
        lastATPWTARanking: data.lastATPWTARanking,
        lastATPWTARankingYear: data.lastATPWTARankingYear,
      },
    });
  });

  const onClear = () => {
    mutate({
      userId: id || user.id,
      updatePlayerATPWTARankingSchema: {
        currentATPWTARanking: null,
        bestATPWTARanking: null,
        bestATPWTARankingYear: null,
        lastATPWTARanking: null,
        lastATPWTARankingYear: null,
      },
    });
  };

  if (status !== "success") {
    return null;
  }

  const ATPWTARankingFormErrors = playerATPWTARankingForm.formState.errors;

  return (
    <>
      <SheetForm id="PlayerATPWTARankingForm" onSubmit={onSubmit}>
        <Controller
          control={playerATPWTARankingForm.control}
          name="isPlayerHaveATPWTARanking"
          rules={{
            onChange: (event) => {
              if (!event.target.value) {
                playerATPWTARankingForm.setValue("currentATPWTARanking", null);
                playerATPWTARankingForm.setValue("bestATPWTARanking", null);
                playerATPWTARankingForm.setValue("bestATPWTARankingYear", null);
                playerATPWTARankingForm.setValue("isCurrentATPWTARankingBest", false);
              }
              if (event.target.value) {
                playerATPWTARankingForm.setValue("lastATPWTARanking", null);
                playerATPWTARankingForm.setValue("lastATPWTARankingYear", null);
                playerATPWTARankingForm.setValue("isCurrentATPWTARankingBest", false);
              }
            },
          }}
          render={({ field }) => (
            <div className="flex items-center justify-between gap-2">
              <Label className="text-base font-medium">
                {t("profile:have-ranking", { ranking: data.gender === "female" ? "WTA" : "ATP" })}
              </Label>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </div>
          )}
        />

        {!playerATPWTARankingForm.watch("isPlayerHaveATPWTARanking") && (
          <div className="grid grid-cols-[1fr_120px] items-end gap-3">
            <div className="flex flex-col gap-4">
              <TextField
                type="number"
                helper={t("profile:ranking-ATP-WTA-ITF-helper")}
                label={t("profile:best-ranking", { ranking: data.gender === "female" ? "WTA" : "ATP" })}
                max={2000}
                min={1}
                {...playerATPWTARankingForm.register("lastATPWTARanking")}
                errorMessage={tDyn(t, ATPWTARankingFormErrors.lastATPWTARanking?.message)}
              />
            </div>

            <TextField
              type="number"
              helper={`1950 à ${currentYear}`}
              label={t("common:year")}
              max={today}
              min={1950}
              {...playerATPWTARankingForm.register("lastATPWTARankingYear")}
              errorMessage={tDyn(t, ATPWTARankingFormErrors.lastATPWTARankingYear?.message)}
            />
          </div>
        )}

        {playerATPWTARankingForm.watch("isPlayerHaveATPWTARanking") && (
          <>
            <TextField
              type="number"
              helper={t("profile:ranking-ATP-WTA-ITF-helper")}
              label={t("profile:current-ranking", { ranking: data.gender === "female" ? "WTA" : "ATP" })}
              max={2000}
              min={1}
              {...playerATPWTARankingForm.register("currentATPWTARanking")}
              errorMessage={tDyn(t, ATPWTARankingFormErrors.currentATPWTARanking?.message)}
            />

            <Controller
              control={playerATPWTARankingForm.control}
              name="isCurrentATPWTARankingBest"
              rules={{
                onChange: (value) => {
                  if (value) {
                    playerATPWTARankingForm.setValue("bestATPWTARanking", null);
                    playerATPWTARankingForm.setValue("bestATPWTARankingYear", null);
                  }
                },
              }}
              render={({ field }) => (
                <RootField>
                  <Checkbox label={t("profile:my-best-ranking")} checked={field.value} onCheckedChange={field.onChange} />
                </RootField>
              )}
            />
          </>
        )}

        {(!playerATPWTARankingForm.watch("isCurrentATPWTARankingBest") && playerATPWTARankingForm.watch("isPlayerHaveATPWTARanking")) && (
          <div className="grid grid-cols-[1fr_120px] items-end gap-3">
            <div className="flex flex-col gap-4">
              <TextField
                type="number"
                helper={t("profile:ranking-ATP-WTA-ITF-helper")}
                label={t("profile:best-ranking", { ranking: data.gender === "female" ? "WTA" : "ATP" })}
                max={2000}
                min={1}
                {...playerATPWTARankingForm.register("bestATPWTARanking")}
                errorMessage={tDyn(t, ATPWTARankingFormErrors.bestATPWTARanking?.message)}
              />
            </div>

            <TextField
              type="number"
              helper={`1950 à ${currentYear}`}
              label={t("common:year")}
              max={today}
              min={1950}
              {...playerATPWTARankingForm.register("bestATPWTARankingYear")}
              errorMessage={tDyn(t, ATPWTARankingFormErrors.bestATPWTARankingYear?.message)}
            />
          </div>
        )}
      </SheetForm>

      { (data.ATPWTARanking || data.lastATPWTARanking)
      && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-ranking")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerATPWTARankingForm" loading={isPending || playerATPWTARankingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function ITFRankingForm({ closeModal }: { closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const { id } = useParams();
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data, status } = useQuery(trpc.player.getPlayer.queryOptions({ playerId: id || user.id }));

  const { mutate, isPending } = useMutation(trpc.player.updateITFRanking.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerITFRankingForm = useForm<UpdatePlayerITFRankingDiscriminatedUnionSchema>({
    resolver: zodResolver(updatePlayerITFRankingDiscriminatedUnionSchema),
    // @ts-expect-error - FFTRanking is null or set, we want to force to undefined
    defaultValues: {
      isPlayerHaveITFRanking: !data?.lastITFRanking,
      isCurrentITFRankingBest: data?.ITFRanking !== null && !data?.bestITFRanking && !data?.bestITFRankingYear,
      currentITFRanking: data?.ITFRanking || null,
      bestITFRanking: data?.bestITFRanking,
      bestITFRankingYear: data?.bestITFRankingYear,
      lastITFRanking: data?.lastITFRanking,
      lastITFRankingYear: data?.lastITFRankingYear,
    },
  });

  const onSubmit = playerITFRankingForm.handleSubmit((data) => {
    mutate({
      userId: id || user.id,
      updatePlayerITFRankingSchema: {
        currentITFRanking: data.currentITFRanking,
        bestITFRanking: data.bestITFRanking,
        bestITFRankingYear: data.bestITFRankingYear,
        lastITFRanking: data.lastITFRanking,
        lastITFRankingYear: data.lastITFRankingYear,
      },
    });
  });

  const onClear = () => {
    mutate({
      userId: id || user.id,
      updatePlayerITFRankingSchema: {
        currentITFRanking: null,
        bestITFRanking: null,
        bestITFRankingYear: null,
        lastITFRanking: null,
        lastITFRankingYear: null,
      },
    });
  };

  if (status !== "success") {
    return null;
  }

  const ITFRankingFormErrors = playerITFRankingForm.formState.errors;

  return (
    <>
      <SheetForm id="PlayerITFRankingForm" onSubmit={onSubmit}>
        <Controller
          control={playerITFRankingForm.control}
          name="isPlayerHaveITFRanking"
          rules={{
            onChange: (event) => {
              if (!event.target.value) {
                playerITFRankingForm.setValue("currentITFRanking", null);
                playerITFRankingForm.setValue("bestITFRanking", null);
                playerITFRankingForm.setValue("bestITFRankingYear", null);
                playerITFRankingForm.setValue("isCurrentITFRankingBest", false);
              }
              if (event.target.value) {
                playerITFRankingForm.setValue("lastITFRanking", null);
                playerITFRankingForm.setValue("lastITFRankingYear", null);
                playerITFRankingForm.setValue("isCurrentITFRankingBest", false);
              }
            },
          }}
          render={({ field }) => (
            <div className="flex items-center justify-between gap-2">
              <Label className="text-base font-medium">
                {t("profile:have-ranking", { ranking: "ITF JR" })}
              </Label>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </div>
          )}
        />

        {!playerITFRankingForm.watch("isPlayerHaveITFRanking") && (
          <div className="grid grid-cols-[1fr_120px] items-end gap-3">
            <div className="flex flex-col gap-4">
              <TextField
                type="number"
                helper={t("profile:ranking-ATP-WTA-ITF-helper")}
                label={t("profile:best-ranking", { ranking: "ITF JR" })}
                max={2000}
                min={1}
                {...playerITFRankingForm.register("lastITFRanking")}
                errorMessage={tDyn(t, ITFRankingFormErrors.lastITFRanking?.message)}
              />
            </div>

            <TextField
              type="number"

              helper={`1950 à ${currentYear}`}
              label={t("common:year")}
              max={today}
              min={1950}
              {...playerITFRankingForm.register("lastITFRankingYear")}
              errorMessage={tDyn(t, ITFRankingFormErrors.lastITFRankingYear?.message)}
            />
          </div>
        )}

        { playerITFRankingForm.watch("isPlayerHaveITFRanking")
        && (
          <>
            <TextField
              type="number"
              helper={t("profile:ranking-ATP-WTA-ITF-helper")}
              label={t("profile:current-ranking", { ranking: "ITF JR" })}
              max={2000}
              min={1}
              {...playerITFRankingForm.register("currentITFRanking")}
              errorMessage={tDyn(t, ITFRankingFormErrors.currentITFRanking?.message)}
            />

            <Controller
              control={playerITFRankingForm.control}
              name="isCurrentITFRankingBest"
              rules={{
                onChange: (value) => {
                  if (value) {
                    playerITFRankingForm.setValue("bestITFRanking", null);
                    playerITFRankingForm.setValue("bestITFRankingYear", null);
                  }
                },
              }}
              render={({ field }) => (
                <RootField>
                  {field.value}
                  <Checkbox label={t("profile:my-best-ranking")} checked={field.value} onCheckedChange={field.onChange} />
                </RootField>
              )}
            />
          </>
        )}

        {(!playerITFRankingForm.watch("isCurrentITFRankingBest") && playerITFRankingForm.watch("isPlayerHaveITFRanking")) && (
          <div className="grid grid-cols-[1fr_120px] items-end gap-3">
            <div className="flex flex-col gap-4">
              <TextField
                type="number"
                helper={t("profile:ranking-ATP-WTA-ITF-helper")}
                label={t("profile:best-ranking", { ranking: "ITF JR" })}
                max={2000}
                min={1}
                {...playerITFRankingForm.register("bestITFRanking")}
                errorMessage={tDyn(t, ITFRankingFormErrors.bestITFRanking?.message)}
              />
            </div>

            <TextField
              type="number"
              helper={`1950 à ${currentYear}`}
              label={t("common:year")}
              max={today}
              min={1950}
              {...playerITFRankingForm.register("bestITFRankingYear")}
              errorMessage={tDyn(t, ITFRankingFormErrors.bestITFRankingYear?.message)}
            />
          </div>
        )}
      </SheetForm>

      { (data.ITFRanking || data.lastITFRanking)
      && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-ranking")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerITFRankingForm" loading={isPending || playerITFRankingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}
